// src/pages/HallOfFame.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Crown,
  Medal,
  Sparkles,
  Trophy,
  Users,
  Star,
  Flame,
  ShieldCheck,
  ArrowUpRight,
  Search,
  Filter,
  ChevronDown,
  RefreshCw,
  BadgeCheck,
  Calendar,
  Zap,
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || "";

function HallOfFame() {
  const [legends, setLegends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI controls
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("rank"); // rank | xp | reviews | badges | friends | newest
  const [showTopOnly, setShowTopOnly] = useState(false); // “Top 3” focus
  const [refreshing, setRefreshing] = useState(false);

  const fetchHallOfFame = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/hall-of-fame`);
      setLegends(Array.isArray(response?.data?.users) ? response.data.users : []);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching Hall of Fame:", err);
      setError("Failed to load Hall of Fame");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHallOfFame();
  }, [fetchHallOfFame]);

  const softRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);
      const response = await axios.get(`${API_URL}/api/hall-of-fame`);
      setLegends(Array.isArray(response?.data?.users) ? response.data.users : []);
    } catch (err) {
      console.error("Error refreshing Hall of Fame:", err);
      setError("Failed to refresh");
    } finally {
      setRefreshing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatNumber = (num) => {
    const n = Number(num || 0);
    return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Derived metrics (safe if missing)
  const totals = useMemo(() => {
    const all = Array.isArray(legends) ? legends : [];
    const sum = (key) => all.reduce((acc, u) => acc + Number(u?.[key] || 0), 0);

    return {
      legendCount: all.length,
      totalXp: sum("total_xp_earned"),
      totalReviews: sum("total_reviews"),
      totalBadges: sum("total_badges"),
      totalFriends: sum("total_friends"),
    };
  }, [legends]);

  // Base filtered list (query + sort + optional top-only)
  const filtered = useMemo(() => {
    let list = Array.isArray(legends) ? [...legends] : [];

    // Query
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((u) => {
        const username = String(u?.username || "").toLowerCase();
        return username.includes(q);
      });
    }

    // Sort
    if (sortBy === "xp") {
      list.sort((a, b) => Number(b?.total_xp_earned || 0) - Number(a?.total_xp_earned || 0));
    } else if (sortBy === "reviews") {
      list.sort((a, b) => Number(b?.total_reviews || 0) - Number(a?.total_reviews || 0));
    } else if (sortBy === "badges") {
      list.sort((a, b) => Number(b?.total_badges || 0) - Number(a?.total_badges || 0));
    } else if (sortBy === "friends") {
      list.sort((a, b) => Number(b?.total_friends || 0) - Number(a?.total_friends || 0));
    } else if (sortBy === "newest") {
      list.sort((a, b) => +new Date(b?.joined_at || 0) - +new Date(a?.joined_at || 0));
    } else {
      // rank: keep backend order
    }

    // Top-only
    if (showTopOnly) list = list.slice(0, 3);

    return list;
  }, [legends, query, sortBy, showTopOnly]);

  // Only show podium when it can’t contradict the list:
  // - no search query
  // - not top-only mode
  // - rank sort (matches backend order)
  const showPodium = useMemo(() => {
    return sortBy === "rank" && !query.trim() && !showTopOnly && filtered.length >= 3;
  }, [sortBy, query, showTopOnly, filtered.length]);

  const top3 = useMemo(() => (showPodium ? filtered.slice(0, 3) : []), [filtered, showPodium]);

  // This is the actual fix: remove podium entries from the grid so they don't appear twice.
  const gridLegends = useMemo(() => {
    if (!showPodium) return filtered;
    return filtered.slice(3);
  }, [filtered, showPodium]);

  // Shared page background (clean and modern)
  const PageShell = ({ children }) => (
    <div className="min-h-screen w-full bg-[#050913] text-white overflow-x-hidden">
      <div className="fixed inset-0 -z-10 bg-[#050913]" />
      <div className="fixed inset-0 -z-10 opacity-[0.12] bg-[radial-gradient(900px_520px_at_18%_10%,rgba(185,246,208,0.14),transparent_60%),radial-gradient(900px_520px_at_88%_12%,rgba(56,189,248,0.08),transparent_60%)]" />
      <div className="fixed inset-0 -z-10 opacity-[0.05] bg-[linear-gradient(to_right,rgba(255,255,255,0.10)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.10)_1px,transparent_1px)] bg-[size:34px_34px]" />
      {children}
    </div>
  );

  const getAvatarSrc = (legend) => {
    const raw = legend?.profile_picture || "";
    if (raw.startsWith("http")) return raw;
    return `${API_URL}${raw || "/avatars/avatar1.png"}`;
  };

  const rankMeta = (index) => {
    if (index === 0) {
      return {
        label: "1",
        icon: Crown,
        ring: "ring-amber-300/25",
        chip: "text-amber-200 border-amber-300/20 bg-white/[0.05]",
      };
    }
    if (index === 1) {
      return {
        label: "2",
        icon: Medal,
        ring: "ring-white/20",
        chip: "text-white/80 border-white/20 bg-white/[0.05]",
      };
    }
    if (index === 2) {
      return {
        label: "3",
        icon: Trophy,
        ring: "ring-orange-300/20",
        chip: "text-orange-200 border-orange-300/20 bg-white/[0.05]",
      };
    }
    return {
      label: String(index + 1),
      icon: Star,
      ring: "ring-white/10",
      chip: "text-white/70 border-white/10 bg-white/[0.04]",
    };
  };

  if (loading) {
    return (
      <PageShell>
        <div className="min-h-screen flex items-center justify-center px-4">
          <div className="text-center rounded-2xl p-6 sm:p-8 bg-white/[0.04] backdrop-blur-2xl border border-white/10 shadow-[0_16px_50px_rgba(0,0,0,0.45)]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B9F6D0] mx-auto"></div>
            <p className="mt-4 text-white/55">Loading…</p>
          </div>
        </div>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
        <div className="min-h-screen px-4 py-8">
          <div className="max-w-3xl mx-auto mt-6">
            <div className="rounded-2xl p-6 text-center bg-white/[0.04] backdrop-blur-2xl border border-red-500/25 shadow-[0_16px_50px_rgba(0,0,0,0.45)]">
              <p className="text-red-300 font-medium">{error}</p>
              <p className="text-white/45 text-sm mt-2">Try refreshing the page or check your server connection.</p>

              <button
                onClick={fetchHallOfFame}
                className="mt-5 inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/10 px-4 py-2 text-sm font-semibold text-white/85 hover:bg-white/15 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Retry
              </button>
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="p-3 sm:p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          {/* TOP */}
          <div className="mt-4 sm:mt-6 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            {/* Left */}
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl px-4 py-3 shadow-[0_16px_50px_rgba(0,0,0,0.35)]">
                <div className="h-10 w-10 rounded-2xl border border-white/10 bg-white/[0.04] grid place-items-center">
                  <Crown className="h-5 w-5 text-[#B9F6D0]" />
                </div>

                <div className="min-w-0">
                  <p className="text-[11px] tracking-[0.32em] uppercase text-white/45">Hall of Fame</p>
                  <div className="flex items-center gap-2">
                    <p className="text-lg sm:text-xl font-extrabold text-white/90 truncate">Legends</p>
                    <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-black/20 px-2.5 py-1 text-[11px] text-white/70">
                      <Sparkles className="h-3.5 w-3.5 text-[#B9F6D0]/85" />
                      Level 50
                    </span>
                  </div>
                </div>
              </div>

              {/* Totals */}
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatPill icon={Users} label="Legends" value={formatNumber(totals.legendCount)} />
                <StatPill icon={Zap} label="Total XP" value={formatNumber(totals.totalXp)} />
                <StatPill icon={Star} label="Reviews" value={formatNumber(totals.totalReviews)} />
                <StatPill icon={BadgeCheck} label="Badges" value={formatNumber(totals.totalBadges)} />
              </div>
            </div>

            {/* Right Controls */}
            <div className="w-full lg:w-[560px]">
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_16px_50px_rgba(0,0,0,0.35)] p-3">
                <div className="flex flex-col sm:flex-row gap-2">
                  {/* Search */}
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search legends…"
                      className="w-full rounded-2xl border border-white/10 bg-[#0B1430] pl-10 pr-3 py-2.5 text-sm text-white/90 placeholder:text-white/35 outline-none focus:border-white/20"
                    />
                  </div>

                  {/* Sort */}
                  <div className="relative w-full sm:w-[240px]">
                    <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="appearance-none w-full rounded-2xl border border-white/10 bg-[#0B1430] pl-10 pr-10 py-2.5 text-sm text-white/90 outline-none focus:border-white/20"
                    >
                      <option value="rank">Sort: Rank</option>
                      <option value="xp">Sort: XP</option>
                      <option value="reviews">Sort: Reviews</option>
                      <option value="badges">Sort: Badges</option>
                      <option value="friends">Sort: Friends</option>
                      <option value="newest">Sort: Newest</option>
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  </div>
                </div>

                <div className="mt-2 flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setShowTopOnly((v) => !v)}
                    className={[
                      "inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-xs font-semibold transition-colors",
                      showTopOnly
                        ? "border-[#B9F6D0]/25 bg-[#B9F6D0]/10 text-white/90"
                        : "border-white/10 bg-white/10 text-white/75 hover:bg-white/15",
                    ].join(" ")}
                    title="Toggle top legends only"
                  >
                    <Trophy className="h-4 w-4 text-[#B9F6D0]/85" />
                    Top 3
                  </button>

                  <div className="flex items-center gap-2">
                    <div className="hidden sm:flex items-center gap-2 text-xs text-white/50">
                      <Flame className="h-4 w-4 text-[#B9F6D0]/80" />
                      <span className="text-white/80 font-semibold">{formatNumber(filtered.length)}</span>
                      <span>shown</span>
                    </div>

                    <button
                      type="button"
                      onClick={softRefresh}
                      disabled={refreshing}
                      className={[
                        "inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-xs font-semibold transition-colors",
                        "border-white/10 bg-white/10 text-white/85 hover:bg-white/15",
                        refreshing ? "opacity-60 cursor-not-allowed" : "",
                      ].join(" ")}
                      title="Refresh"
                    >
                      <RefreshCw className={["h-4 w-4", refreshing ? "animate-spin" : ""].join(" ")} />
                      Refresh
                    </button>
                  </div>
                </div>

                {/* tiny note for podium logic (kept subtle & professional) */}
                {sortBy !== "rank" && !query.trim() && !showTopOnly && (
                  <p className="mt-2 text-[11px] text-white/35">
                    Podium appears on Rank sort.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* TOP 3: podium (rank sort only; no duplicates below) */}
          {showPodium && top3.length > 0 && (
            <div className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {top3.map((u, idx) => (
                  <TopLegendCard
                    key={u.user_id}
                    legend={u}
                    index={idx}
                    avatarSrc={getAvatarSrc(u)}
                    formatNumber={formatNumber}
                    formatDate={formatDate}
                  />
                ))}
              </div>
            </div>
          )}

          {/* MAIN GRID */}
          <div className="mt-6 sm:mt-8">
            {filtered.length === 0 ? (
              <div className="text-center rounded-2xl p-8 sm:p-10 bg-white/[0.04] backdrop-blur-2xl border border-white/10 shadow-[0_16px_50px_rgba(0,0,0,0.45)]">
                <div className="mx-auto mb-4 grid place-items-center h-14 w-14 rounded-2xl border border-white/10 bg-white/[0.04]">
                  <Trophy className="h-6 w-6 text-[#B9F6D0]/85" />
                </div>
                <p className="text-xl text-white/80 font-extrabold">No legends found</p>
                <p className="text-white/45 mt-2">Try a different search or clear filters.</p>
              </div>
            ) : (
              <>
                {/* If podium is showing, you might still want the rest of the list clearly separated */}
                {showPodium && gridLegends.length > 0 && (
                  <div className="mb-4 flex items-center justify-between">
                    <p className="text-xs tracking-[0.24em] uppercase text-white/35">
                      More legends
                    </p>
                    <div className="hidden sm:flex items-center gap-2 text-xs text-white/45">
                      <Flame className="h-4 w-4 text-[#B9F6D0]/70" />
                      <span>{formatNumber(gridLegends.length)} listed</span>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {(showPodium ? gridLegends : filtered).map((legend, index) => {
                    // Keep rank badges accurate to overall ranking even when podium is removed:
                    const globalIndex = showPodium ? index + 3 : index;

                    return (
                      <LegendCard
                        key={legend.user_id}
                        legend={legend}
                        index={globalIndex}
                        avatarSrc={getAvatarSrc(legend)}
                        rankMeta={rankMeta(globalIndex)}
                        formatNumber={formatNumber}
                        formatDate={formatDate}
                      />
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <div className="h-10" />
        </div>
      </div>
    </PageShell>
  );
}

/* ---------- UI Components ---------- */

function StatPill({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_16px_50px_rgba(0,0,0,0.25)] p-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-white/45">{label}</p>
        <Icon className="h-4 w-4 text-white/40" />
      </div>
      <p className="mt-2 text-lg font-extrabold text-white/90">{value}</p>
    </div>
  );
}

function LegendCard({ legend, index, avatarSrc, rankMeta, formatNumber, formatDate }) {
  const RankIcon = rankMeta.icon;
  const top = index < 3;

  return (
    <div className="relative overflow-hidden rounded-2xl p-5 sm:p-6 bg-white/[0.04] backdrop-blur-2xl border border-white/10 shadow-[0_16px_50px_rgba(0,0,0,0.45)] hover:bg-white/[0.06] transition-all">
      <div className="pointer-events-none absolute -top-24 left-1/2 h-40 w-[520px] -translate-x-1/2 rounded-full bg-[#B9F6D0]/10 blur-3xl opacity-40" />

      {/* Rank chip */}
      <div className="absolute top-4 right-4">
        <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-semibold ${rankMeta.chip}`}>
          <RankIcon className="h-4 w-4" />
          #{rankMeta.label}
        </div>
      </div>

      <Link to={`/hub/${legend.user_id}`} className="block">
        <div className="flex items-center gap-4">
          <img
            src={avatarSrc}
            alt={legend.username}
            className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover border border-white/15 ring-2 ${rankMeta.ring}`}
          />
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-xl font-extrabold text-white truncate hover:text-[#B9F6D0] transition-colors">
              {legend.username}
            </h2>
            <div className="mt-1 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-3 py-1">
              <Crown className="h-4 w-4 text-[#B9F6D0]/85" />
              <span className="text-[#B9F6D0] font-semibold text-sm">Level {legend.level}</span>
            </div>
          </div>
        </div>
      </Link>

      {/* Stats */}
      <div className="mt-5 grid grid-cols-2 gap-3">
        <MiniStat icon={Zap} label="Total XP" value={formatNumber(legend.total_xp_earned)} />
        <MiniStat icon={Star} label="Reviews" value={formatNumber(legend.total_reviews)} />
        <MiniStat icon={BadgeCheck} label="Badges" value={formatNumber(legend.total_badges)} />
        <MiniStat icon={Users} label="Friends" value={formatNumber(legend.total_friends)} />
      </div>

      {/* XP Sources */}
      <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-3">
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-white/45">XP Sources</p>
          {top && (
            <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] text-white/70">
              <ShieldCheck className="h-3.5 w-3.5 text-[#B9F6D0]/80" />
              Verified legend
            </span>
          )}
        </div>

        <div className="mt-2 space-y-1">
          <XpRow label="Reviews" value={formatNumber(legend.xp_from_reviews)} accent="text-[#B9F6D0]" />
          <XpRow label="Badges" value={formatNumber(legend.xp_from_badges)} accent="text-cyan-200" />
          <XpRow label="Social" value={formatNumber(legend.xp_from_social)} accent="text-white/70" />
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 flex items-center justify-between text-xs text-white/40">
        <div className="inline-flex items-center gap-2">
          <Calendar className="h-4 w-4 text-white/35" />
          <span>Since {formatDate(legend.joined_at)}</span>
        </div>

        <span className="inline-flex items-center gap-1 text-white/55 hover:text-white/80 transition-colors">
          View
          <ArrowUpRight className="h-4 w-4" />
        </span>
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3">
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-white/45">{label}</p>
        <Icon className="h-4 w-4 text-white/35" />
      </div>
      <p className="mt-2 text-lg font-extrabold text-white/90">{value}</p>
    </div>
  );
}

function XpRow({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-white/45">{label}</span>
      <span className={`${accent}`}>{value} XP</span>
    </div>
  );
}

function TopLegendCard({ legend, index, avatarSrc, formatNumber, formatDate }) {
  const meta = [
    { title: "Champion", sub: "Rank #1", ring: "ring-amber-300/25", chip: "border-amber-300/20 text-amber-200", icon: Crown },
    { title: "Runner-up", sub: "Rank #2", ring: "ring-white/20", chip: "border-white/20 text-white/80", icon: Medal },
    { title: "Third", sub: "Rank #3", ring: "ring-orange-300/20", chip: "border-orange-300/20 text-orange-200", icon: Trophy },
  ][index];

  const Icon = meta.icon;

  return (
    <div className="relative overflow-hidden rounded-2xl p-6 bg-white/[0.04] backdrop-blur-2xl border border-white/10 shadow-[0_16px_50px_rgba(0,0,0,0.45)] hover:bg-white/[0.06] transition-all">
      <div className="pointer-events-none absolute -top-24 left-1/2 h-40 w-[520px] -translate-x-1/2 rounded-full bg-[#B9F6D0]/10 blur-3xl opacity-40" />

      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] tracking-[0.32em] uppercase text-white/45">{meta.title}</p>
          <div className={`mt-2 inline-flex items-center gap-2 rounded-full border bg-black/20 px-3 py-1 ${meta.chip}`}>
            <Icon className="h-4 w-4" />
            <span className="text-sm font-semibold">{meta.sub}</span>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-2">
          <Sparkles className="h-5 w-5 text-[#B9F6D0]/85" />
        </div>
      </div>

      <Link to={`/hub/${legend.user_id}`} className="mt-5 block">
        <div className="flex items-center gap-4">
          <img
            src={avatarSrc}
            alt={legend.username}
            className={`w-16 h-16 rounded-full object-cover border border-white/15 ring-2 ${meta.ring}`}
          />
          <div className="min-w-0 flex-1">
            <p className="text-xl font-extrabold text-white truncate hover:text-[#B9F6D0] transition-colors">
              {legend.username}
            </p>
            <div className="mt-1 inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-3 py-1">
              <Crown className="h-4 w-4 text-[#B9F6D0]/85" />
              <span className="text-[#B9F6D0] font-semibold text-sm">Level {legend.level}</span>
            </div>
          </div>
        </div>
      </Link>

      <div className="mt-5 grid grid-cols-3 gap-3">
        <MiniStat icon={Zap} label="XP" value={formatNumber(legend.total_xp_earned)} />
        <MiniStat icon={Star} label="Reviews" value={formatNumber(legend.total_reviews)} />
        <MiniStat icon={Users} label="Friends" value={formatNumber(legend.total_friends)} />
      </div>

      <div className="mt-4 flex items-center justify-between text-xs text-white/40">
        <span className="inline-flex items-center gap-2">
          <Calendar className="h-4 w-4 text-white/35" />
          Since {formatDate(legend.joined_at)}
        </span>
        <span className="inline-flex items-center gap-1 text-white/55 hover:text-white/80 transition-colors">
          View
          <ArrowUpRight className="h-4 w-4" />
        </span>
      </div>
    </div>
  );
}

export default HallOfFame;
